import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/common';

interface FormValues {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      token: tokenFromUrl,
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      token: (value) => (value.length > 0 ? null : 'Reset token is required'),
      newPassword: (value) => {
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
        if (!/\d/.test(value)) return 'Password must contain a number';
        if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain a special character';
        return null;
      },
      confirmPassword: (value, values) =>
        value === values.newPassword ? null : 'Passwords do not match',
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (values: FormValues) => resetPassword(values.token, values.newPassword),
    onSuccess: () => setSuccess(true),
    onError: (err) => setError(err instanceof Error ? err.message : 'Invalid or expired reset token. Please try again.'),
  });

  const handleSubmit = (values: FormValues) => {
    setError('');
    resetPasswordMutation.mutate(values);
  };

  if (success) {
    return (
      <Container size={420} my={40}>
        <Stack align="center" mb="lg">
          <Logo size="lg" />
        </Stack>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack align="center" gap="md">
            <IconCircleCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3} ta="center">
              Password Reset!
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Your password has been successfully reset. You can now sign in with your new password.
            </Text>
            <Button component={Link} to="/login" fullWidth>
              Sign In
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="lg">
        <Logo size="lg" />
        <Title order={2} ta="center">
          Reset Password
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Enter the reset token from your email and choose a new password.
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {!tokenFromUrl && (
              <TextInput
                label="Reset Code"
                placeholder="A3X9KP2M"
                required
                styles={{ input: { textTransform: 'uppercase', letterSpacing: 4, fontWeight: 700, textAlign: 'center', fontSize: 18 } }}
                maxLength={8}
                {...form.getInputProps('token')}
                onChange={(e) => form.setFieldValue('token', e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
              />
            )}

            <PasswordInput
              label="New Password"
              placeholder="Create a new password"
              required
              {...form.getInputProps('newPassword')}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your new password"
              required
              {...form.getInputProps('confirmPassword')}
            />

            <Button type="submit" fullWidth loading={resetPasswordMutation.isPending}>
              Reset Password
            </Button>
          </Stack>
        </form>

        <Text c="dimmed" size="sm" ta="center" mt="md">
          Remember your password?{' '}
          <Anchor component={Link} to="/login" size="sm">
            Log in
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
